import { useEffect } from "react";
import { useCouvre } from "./messager.js";
import type { TribePrivate } from "@hoa/protocol";
import { keepAwake } from "../adapters/geo.js";
import { Avatar, Croix } from "./ui.js";

/**
 * La balise : au rendez-vous, la couleur de la tribu en plein écran.
 *
 * On lève son téléphone, les autres cherchent cette couleur dans la foule.
 * Elle s'ouvre seule à l'arrivée (« J'y suis »), se referme seule quand tout
 * le monde est là, et se rouvre d'un appui sur « Montrer ma couleur ».
 *
 * Deux interrupteurs : la régie pour tous (`couleurArrivee` de la tribu),
 * chacun pour soi (Paramètres). Il faut les deux.
 */

const CLE = "hoa.balise";

export function baliseVoulue(): boolean {
  try {
    return localStorage.getItem(CLE) !== "0";
  } catch {
    return true;
  }
}

export function ecrireBalise(oui: boolean): void {
  try {
    if (oui) localStorage.removeItem(CLE);
    else localStorage.setItem(CLE, "0");
  } catch {
    /* navigation privée : le réglage vaut pour la visite */
  }
}

/** La régie l'autorise et la personne la veut. */
export function baliseActive(t: TribePrivate): boolean {
  return t.couleurArrivee !== false && baliseVoulue();
}

/** Texte foncé sur une couleur claire, blanc sur une couleur foncée (luminance relative WCAG). */
export function encreSur(couleur: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(couleur.trim());
  if (!m) return "#111111";
  const n = parseInt(m[1]!, 16);
  const canal = (c: number): number => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const l = 0.2126 * canal((n >> 16) & 255) + 0.7152 * canal((n >> 8) & 255) + 0.0722 * canal(n & 255);
  // Contraste avec le noir (#111) contre contraste avec le blanc.
  return (l + 0.05) / 0.0555 >= 1.05 / (l + 0.05) ? "#111111" : "#FFFFFF";
}

export function Balise(props: { tribe: TribePrivate; photos: (pid: string) => string | null; onFermer: () => void }): JSX.Element {
  const t = props.tribe;
  const encre = encreSur(t.color);
  const arrives = t.members.filter((m) => m.arrive);
  const attendus = t.members.length;
  useCouvre();

  // L'écran reste allumé, et la barre du navigateur prend la couleur.
  useEffect(() => {
    let relacher: (() => void) | null = null;
    void keepAwake().then((r) => (relacher = r));
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const avant = meta?.content ?? null;
    if (meta) meta.content = t.color;
    const touche = (e: KeyboardEvent): void => {
      if (e.key === "Escape") props.onFermer();
    };
    addEventListener("keydown", touche);
    return () => {
      relacher?.();
      if (meta && avant !== null) meta.content = avant;
      removeEventListener("keydown", touche);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t.color]);

  return (
    <div className="hoa-balise" role="dialog" aria-modal="true" aria-label={`Couleur de la tribu ${t.tribeName}`} style={{ background: t.color, color: encre }}>
      <div className="haut">
        <span className="compte">
          {arrives.length} sur {attendus} sur place
        </span>
        <button type="button" className="fermer" aria-label="Fermer" style={{ color: encre, borderColor: encre }} onClick={props.onFermer}>
          <Croix />
        </button>
      </div>
      <div className="centre">
        <span className="pouls" aria-hidden="true" style={{ borderColor: encre }} />
        <b className="nom">{t.tribeName}</b>
        <span className="signal">{t.signal}</span>
      </div>
      <div className="bas">
        <div className="visages" aria-label={`${arrives.length} ${arrives.length > 1 ? "arrivés" : "arrivé"}`}>
          {t.members.map((m) => (
            <span key={m.pid} className={m.arrive ? "visage la" : "visage"} title={m.moi ? "Toi" : m.pseudo}>
              <Avatar src={props.photos(m.pid)} nom={m.pseudo} taille={40} />
            </span>
          ))}
        </div>
        <span className="consigne">Lève ton téléphone : ta tribu cherche cette couleur.</span>
      </div>
    </div>
  );
}
